import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Share2, Home, ArrowRight, Gift, Diamond, ShieldCheck, Crown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import { shopService } from '../../services/shopService';

const RewardPage = () => {
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);

  useEffect(() => {
    const fetchShop = async () => {
      const shopId = sessionStorage.getItem('currentShopId');
      if (shopId) {
        const data = await shopService.getShopById(shopId);
        setShop(data);
      }
    };
    fetchShop();

    const duration = 5 * 1000;
    const end = Date.now() + duration;

    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: ['#d4af37', '#fcf6ba', '#b8860b']
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: ['#d4af37', '#fcf6ba', '#b8860b']
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    frame();
  }, []);

  const handleShare = () => {
    if (shop?.gmbLink) {
      window.open(shop.gmbLink, '_blank');
    } else {
      // Fallback or just do nothing if not set
      console.log("GMB Link not configured");
    }
  };

  return (
    <div className="min-h-screen bg-[#fdfdfd] flex flex-col items-center justify-center p-6 relative overflow-hidden">
      <div className="absolute inset-0 luxury-grid-bg pointer-events-none" />
      
      {/* Radiant Background Effects */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[#d4af37]/5 rounded-full blur-[300px] animate-pulse" />
      
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 100, damping: 20 }}
        className="luxury-card w-full max-w-2xl p-16 text-center relative z-10 overflow-hidden"
      >
        {/* Animated Decorative Element */}
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-transparent via-[#d4af37] to-transparent animate-shimmer" />
        
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", damping: 12, stiffness: 200, delay: 0.2 }}
          className="w-28 h-28 rounded-[2.5rem] bg-black flex items-center justify-center text-[#d4af37] mx-auto mb-10 shadow-2xl relative group"
        >
          <div className="absolute inset-0 animate-pulse bg-[#d4af37]/20 blur-2xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
          <CheckCircle2 size={56} strokeWidth={2.5} className="relative z-10" />
        </motion.div>

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="space-y-6"
        >
          <button 
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-3 px-8 py-3 rounded-full bg-black text-white text-[10px] font-black uppercase tracking-[0.5em] mb-4 shadow-xl relative overflow-hidden active:scale-95 transition-transform"
          >
            <div className="absolute inset-0 animate-shimmer opacity-20" />
            <ShieldCheck size={14} className="text-[#d4af37] relative" />
            <span className="relative">REDEMPTION SECURE</span>
          </button>
          
          <h1 className="text-5xl md:text-7xl font-serif text-black mb-6 tracking-tighter leading-tight italic">Victory <br /><span className="luxury-text">Claimed.</span></h1>
          
          <div className="py-10 px-6 md:px-8 bg-black/[0.03] rounded-[2.5rem] border border-black/[0.05] mb-12 relative overflow-hidden group">
            <div className="absolute inset-0 animate-shimmer opacity-5" />
            <p className="text-[#999] text-[10px] font-bold uppercase tracking-[0.4em] mb-2">Vault Receipt</p>
            <h2 className="text-2xl md:text-3xl font-serif text-black break-words">Redemption Successful</h2>
            <div className="mt-6 flex justify-center gap-4">
              <div className="px-4 py-1.5 rounded-full bg-emerald-500/5 text-emerald-600 text-[8px] font-black uppercase tracking-widest border border-emerald-500/10">
                Verified Asset
              </div>
              <div className="px-4 py-1.5 rounded-full bg-blue-500/5 text-blue-600 text-[8px] font-black uppercase tracking-widest border border-blue-500/10">
                DS-SYNC-99
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="flex flex-col sm:flex-row gap-6 justify-center mt-12"
        >
          <button 
            onClick={() => navigate('/')}
            className="luxury-button flex items-center justify-center gap-4 !px-12 !py-5 min-w-[240px]"
          >
            <Home size={18} />
            Return to Hub
          </button>
          <button 
            onClick={handleShare}
            className="luxury-outline flex items-center justify-center gap-4 !px-12 !py-5 min-w-[240px] hover:bg-black hover:text-white"
          >
            <Share2 size={18} />
            Share Privilege
          </button>
        </motion.div>

        {/* Decorative Corner Icons */}
        <div className="absolute top-12 left-12 text-black/[0.03] animate-luxury-float"><Diamond size={48} /></div>
        <div className="absolute bottom-12 right-12 text-black/[0.03] animate-luxury-float" style={{ animationDelay: '1s' }}><Crown size={48} /></div>
      </motion.div>

      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="mt-20 flex flex-col items-center gap-4 opacity-30"
      >
        <div className="flex gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="w-1.5 h-1.5 bg-black rounded-full" />
          ))}
        </div>
        <p className="text-[9px] font-black uppercase tracking-[0.6em] text-black">© 2026 DIGI SAHYADRI SECURED NETWORK</p>
      </motion.div>
    </div>
  );
};

export default RewardPage;
